import {
  isProductFiltered,
  SheetTabFilterQuery,
} from "@/features/order-sheet-sets/sheet-tab-filter";
import useIdentity from "@/features/ui/hooks/use-identity";
import { useEffect, useMemo, useRef } from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import {
  ListAllLineSheetTabsPathParameter,
  ListAllLineSheetTabsRequest,
  useListAllLineSheetTabs,
} from "@/features/line-sheet-sets/hooks/use-list-all-line-sheet-tabs";
import { useSetRecoilState } from "recoil";
import {
  InflatedProduct,
  inflatedTabsFamily,
} from "@/features/line-sheet-sets/helpers/sheet-state";

export interface UseFetchAndSetLineSheetTabsParameter {
  data:
    | {
        lineSheetSetId: number;
        lineSheetId: number;
        tabIndexes: number[];
      }
    | undefined;
  query?: SheetTabFilterQuery;
}

export default function useFetchAndSetLineSheetTabs({
  data,
  query,
}: UseFetchAndSetLineSheetTabsParameter) {
  const identity = useIdentity();
  const company = useMemo(() => {
    return identity?.company;
  }, [identity]);
  const { error: showError } = useAppToasts();

  const listAllLineSheetTabsKey = useMemo(():
    | {
        parameter: ListAllLineSheetTabsPathParameter;
        request: ListAllLineSheetTabsRequest;
      }
    | undefined => {
    if (data && company) {
      return {
        parameter: {
          by: company.type,
          companyId: company.id,
          lineSheetSetId: data.lineSheetSetId,
          lineSheetId: data.lineSheetId,
        },
        request: {
          tabIndexes: data.tabIndexes,
        },
      };
    }

    return undefined;
  }, [data, company]);

  const {
    isLoading: isListAllLineSheetTabsLoading,
    isValidating: isListAllLineSheetTabsValidating,
    data: listAllLineSheetTabsData,
    error: listAllLineSheetTabsError,
    mutate: mutateListAllLineSheetTabs,
  } = useListAllLineSheetTabs(
    listAllLineSheetTabsKey?.parameter,
    listAllLineSheetTabsKey?.request,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    showError(listAllLineSheetTabsError);
  }, [showError, listAllLineSheetTabsError]);

  const setInflatedTabsFamily = useSetRecoilState(
    inflatedTabsFamily({
      lineSheetSetId: data?.lineSheetSetId || -1,
      lineSheetId: data?.lineSheetId || -1,
    })
  );

  // query를 ref로 관리: API 데이터 변경 시에만 전체 초기화, 필터 변경은 별도 처리
  const queryRef = useRef(query);
  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    if (
      !isListAllLineSheetTabsLoading &&
      !isListAllLineSheetTabsValidating &&
      listAllLineSheetTabsData
    ) {
      const currentQuery = queryRef.current;
      setInflatedTabsFamily({
        key: {
          lineSheetSetId: listAllLineSheetTabsData._lineSheetSetId,
          lineSheetId: listAllLineSheetTabsData._lineSheetId,
        },
        tabs: listAllLineSheetTabsData.tabs.map((tab) => {
          return {
            ...tab,
            products: tab.products.sort(
              (a, b) => {
                if (a.supplyPrice !== null && b.supplyPrice !== null) {
                  if (currentQuery?.orderBy === "price_asc") {
                    return a.supplyPrice.amount - b.supplyPrice.amount;
                  } else if (currentQuery?.orderBy === "price_desc") {
                    return b.supplyPrice.amount - a.supplyPrice.amount;
                  } else {
                    return a.id - b.id;
                  }
                } else {
                  return 0;
                }
              }
            ).map((product): InflatedProduct => {
              return {
                ...product,
                priceAdjustedBy: {
                  ...product.priceAdjustedBy,
                  value: product.priceAdjustedBy.value,
                },
                isFiltered: isProductFiltered(product, currentQuery),
                isDirty: false,
                orderQuantityWithOptionList:
                  product.stockQuantityWithOptionList.map((i) => {
                    return {
                      productOptionId: i.productOptionId,
                      quantity: 0,
                      name: i.name,
                    };
                  }),
                firstOrderQuantityWithOptionList: null,
                prevOrderQuantityWithOptionList: null,
                latestOrderQuantityWithOptionList: null,
                confirmedQuantityWithOptionList: null,
                confirmedPrice: null,
                confirmedPriceWithoutCondition: null,
                confirmedPriceAdjustedBy: null,
                newStockWithOptionList: null,
              };
            }),
            isDirty: false,
          };
        }),
      });
    }
  }, [
    isListAllLineSheetTabsLoading,
    listAllLineSheetTabsData,
    setInflatedTabsFamily,
    isListAllLineSheetTabsValidating,
  ]);

  return {
    mutateListAllLineSheetTabs,
  };
}
